import { apiService } from './api';
import { autoRoundPrice } from '../utils/priceRounding';

/** API base including `/api` (same as axios client). */
function getOrdersApiBase(): string {
  const isLocalBrowser =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  // In local dev always use local backend, even if REACT_APP_API_URL points to production.
  if (isLocalBrowser || process.env.NODE_ENV !== 'production') {
    return 'http://localhost:3001/api';
  }

  const fromEnv = process.env.REACT_APP_API_URL?.trim();
  if (fromEnv) return fromEnv.replace(/\/$/, '');
  return 'https://links.usehypwave.com/api';
}

/** Origin for static files at `/uploads/...` (no `/api` prefix). */
function getStaticOrigin(): string {
  const base = getOrdersApiBase();
  return base.replace(/\/?api\/?$/i, '').replace(/\/$/, '') || 'http://localhost:3001';
}

export interface GuestBlogOrder {
  id: string;
  orderId: string;
  clientId: string;
  projectId: string;
  guestBlogSiteId: string;
  price: number;
  submissionType: 'GBS' | 'LI' | 'BOTH';
  contentText?: string;
  contentDocUrl?: string;
  contentUrl?: string;
  status: 'ACTIVE' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  paymentAttemptId?: string | null;
  orderDate?: string; // ISO date string
  createdAt: string;
  updatedAt: string;
  linkedPlacement?: {
    id: string;
    placementId: string;
    originalOrderId: string;
    originalOrderNumber: string;
    status: string;
    liveUrl?: string | null;
    placedAt?: string | null;
    orderDate?: string | null;
    createdAt: string;
    updatedAt: string;
    guestBlogSite?: {
      id: string;
      site_url: string;
    };
  } | null;
  paymentAttempt?: {
    id: string;
    provider: string;
    status: string;
    amount: number;
    currency: string;
    providerSessionId?: string | null;
    providerPaymentIntentId?: string | null;
    finalizedAt?: string | null;
    createdAt: string;
  } | null;
  client: {
    id: string;
    name: string;
    email: string;
    percentage?: number;
  };
  project: {
    id: string;
    projectName: string;
    websiteUrl?: string;
    companyName?: string;
  };
  guestBlogSite: {
    id: string;
    site_url: string;
    da: number;
    dr: number;
    category: string;
    base_price: number;
    li_base_price?: number;
    publisher: {
      id: string;
      publisherName: string;
      email?: string;
    };
  };
  publishers?: {
    id: string;
    publisherName: string;
    email?: string;
  };
  createdBy: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

export interface CartItem {
  guestBlogSiteId: string;
  clientId: string;
  projectId: string;
  price: number;
  submissionType: 'GBS' | 'LI' | 'BOTH';
  contentText?: string;
  contentDocUrl?: string;
  contentUrl?: string;
  orderDate?: string; // ISO date string
}

export interface CreateOrderItem {
  guestBlogSiteId: string;
  submissionType: 'GBS' | 'LI' | 'BOTH';
  orderDate?: string; // ISO date string
}

export interface CreateOrdersRequest {
  cartItems: CreateOrderItem[];
}

export interface UpdateOrderRequest {
  contentText?: string;
  status?: 'ACTIVE' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  contentDocUrl?: string;
  contentUrl?: string;
}

export interface GetOrdersParams {
  page?: number;
  limit?: number;
  status?: string;
  clientId?: string;
  projectId?: string;
  search?: string;
  month?: number;
  year?: number;
}

export interface OrdersResponse {
  success: boolean;
  data: GuestBlogOrder[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface OrderFilterOptionsResponse {
  success: boolean;
  data: {
    clients: Array<{ id: string; name: string }>;
    projects: Array<{ id: string; projectName: string }>;
  };
}

export interface FileUploadResponse {
  success: boolean;
  data: {
    url: string;
    filename: string;
    originalName: string;
    size: number;
  };
}

class GuestBlogOrderService {
  private baseUrl = '/guest-blog-orders';

  async getOrders(params: GetOrdersParams = {}): Promise<OrdersResponse> {
    const searchParams = new URLSearchParams();
    
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.status) searchParams.append('status', params.status);
    if (params.clientId) searchParams.append('clientId', params.clientId);
    if (params.projectId) searchParams.append('projectId', params.projectId);
    if (params.search) searchParams.append('search', params.search);
    if (params.month) searchParams.append('month', params.month.toString());
    if (params.year) searchParams.append('year', params.year.toString());

    const queryString = searchParams.toString();
    const url = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;
    
    return await apiService.get<OrdersResponse>(url);
  }

  async getFilterOptions(params: Pick<GetOrdersParams, 'status' | 'clientId' | 'month' | 'year'> = {}): Promise<OrderFilterOptionsResponse> {
    const searchParams = new URLSearchParams();

    if (params.status) searchParams.append('status', params.status);
    if (params.clientId) searchParams.append('clientId', params.clientId);
    if (params.month) searchParams.append('month', params.month.toString());
    if (params.year) searchParams.append('year', params.year.toString());

    const queryString = searchParams.toString();
    const url = queryString ? `${this.baseUrl}/filter-options?${queryString}` : `${this.baseUrl}/filter-options`;

    return await apiService.get<OrderFilterOptionsResponse>(url);
  }

  async createOrders(data: CreateOrdersRequest): Promise<{ success: boolean; data: GuestBlogOrder[]; message: string }> {
    console.log('GuestBlogOrderService - Creating orders with data:', JSON.stringify(data, null, 2));
    try {
      const response = await apiService.post<{ success: boolean; data: GuestBlogOrder[]; message: string }>(this.baseUrl, data);
      console.log('GuestBlogOrderService - Success response:', response);
      return response;
    } catch (error: any) {
      console.error('GuestBlogOrderService - Error response:', error.response?.data);
      console.error('GuestBlogOrderService - Validation errors:', error.response?.data?.errors);
      throw error;
    }
  }

  async updateOrder(id: string, data: UpdateOrderRequest): Promise<{ success: boolean; data: GuestBlogOrder; message: string }> {
    return await apiService.put<{ success: boolean; data: GuestBlogOrder; message: string }>(`${this.baseUrl}/${id}`, data);
  }

  async uploadContentDocument(file: File): Promise<FileUploadResponse> {
    const formData = new FormData();
    formData.append('contentDoc', file);
    return await apiService.upload<FileUploadResponse>(`${this.baseUrl}/upload-content-doc`, formData);
  }

  async deleteOrder(id: string): Promise<{ success: boolean; message: string }> {
    return await apiService.delete<{ success: boolean; message: string }>(`${this.baseUrl}/${id}`);
  }

  async placeBulkOrders(orderIds: string[]): Promise<{ success: boolean; data: any[]; message: string }> {
    return await apiService.post<{ success: boolean; data: any[]; message: string }>(`${this.baseUrl}/bulk/place`, { orderIds });
  }

  async getOrderedSites(clientId: string): Promise<{ success: boolean; data: Array<{ siteId: string; projectId: string }> }> {
    return await apiService.get<{ success: boolean; data: Array<{ siteId: string; projectId: string }> }>(`${this.baseUrl}/ordered-sites/${clientId}`);
  }

  formatPrice(price: number): string {
    const roundedPrice = autoRoundPrice(price);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(roundedPrice);
  }

  getStatusColor(status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' {
    switch (status) {
      case 'CART':
        return 'warning';
      case 'ACTIVE':
        return 'success';
      case 'IN_PROGRESS':
        return 'info';
      case 'COMPLETED':
        return 'success';
      case 'CANCELLED':
        return 'error';
      default:
        return 'default';
    }
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'CART':
        return 'Cart';
      case 'ACTIVE':
        return 'Active';
      case 'IN_PROGRESS':
        return 'In Progress';
      case 'COMPLETED':
        return 'Completed';
      case 'CANCELLED':
        return 'Cancelled';
      default:
        return status;
    }
  }

  // Utility function to get full file URL
  getFileUrl(relativePath: string): string {
    if (!relativePath) return '';
    if (relativePath.startsWith('http')) return relativePath;
    const origin = getStaticOrigin();
    const pathPart = relativePath.startsWith('/') ? relativePath : `/${relativePath}`;
    return `${origin}${pathPart}`;
  }

  // Utility function to get preview URL for documents
  getPreviewUrl(relativePath: string): string {
    if (!relativePath) return '';
    const file = encodeURIComponent(this.getFileName(relativePath));
    const base = getOrdersApiBase();
    return `${base}/guest-blog-orders/preview-doc?token=preview-access&file=${file}`;
  }

  // Extract DOCX content as HTML
  async extractDocxContent(relativePath: string): Promise<{ success: boolean; content?: string; error?: string }> {
    if (!relativePath) return { success: false, error: 'No file path provided' };
    
    try {
      const filename = this.getFileName(relativePath);
      const file = encodeURIComponent(filename);
      const extractUrl = `${getOrdersApiBase()}/guest-blog-orders/extract-doc?token=preview-access&file=${file}`;
      
      const response = await fetch(extractUrl);
      const text = await response.text();
      let result: { success: boolean; content?: string; error?: string };
      try {
        result = JSON.parse(text);
      } catch {
        return {
          success: false,
          error: response.ok ? 'Invalid response from server' : `Preview failed (${response.status})`,
        };
      }
      
      return result;
    } catch (error) {
      console.error('Error extracting DOCX content:', error);
      return { success: false, error: 'Failed to extract document content' };
    }
  }

  // Utility function to get filename from URL
  getFileName(fileUrl: string): string {
    if (!fileUrl) return 'document';
    const parts = fileUrl.split('/');
    return parts[parts.length - 1] || 'document';
  }

  // Function to download file
  async downloadFile(fileUrl: string, fileName?: string): Promise<void> {
    if (!fileUrl) return;
    
    const downloadName = fileName || this.getFileName(fileUrl);
    const apiBase = getOrdersApiBase();
    
    try {
      const file = encodeURIComponent(this.getFileName(fileUrl));
      const downloadUrl = `${apiBase}/guest-blog-orders/download-doc?token=download-access&file=${file}`;
      
      // Create a temporary link element and trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = downloadName;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading file:', error);
      const file = encodeURIComponent(this.getFileName(fileUrl));
      const fallbackUrl = `${apiBase}/guest-blog-orders/preview-doc?token=preview-access&file=${file}`;
      window.open(fallbackUrl, '_blank');
    }
  }
}

export const guestBlogOrderService = new GuestBlogOrderService();